import React from "react";
import {
  Box, Typography, Stack,
  Button, Divider, Tooltip, Paper,
  Fade,
  useTheme
} from "@mui/material";

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EqualizerIcon from '@mui/icons-material/Equalizer'; 
import StarOutlineIcon from '@mui/icons-material/StarOutline'; 

const QuestionFilterComponent = ({
  groupsById,
  selectedVariable,
  preferenceFilter,
  onPreferenceFilterChange,
  ActionMode,
  side,
  selectedFeatureData,
}) => {
  const currentVar = groupsById?.[selectedVariable];

  // Sort keys descending by national rate
  const sortedKeys = React.useMemo(() => {
    const keys = currentVar?.["keys"] || [];
    const national = selectedFeatureData?.national;
    if (!national?.length) return keys;
    const natMap = new Map();
    national.forEach(({ key, value }) => { if (key) natMap.set(key, Number(value) || 0); });
    return [...keys].sort((a, b) => (natMap.get(b) ?? -Infinity) - (natMap.get(a) ?? -Infinity));
  }, [currentVar, selectedFeatureData]);

  // --- LOGIC HELPERS ---
  const toggleVal = (curr, next, OG) => (curr === next ? OG : next);
  
  // UPDATED: Logic to fallback to 'net' for likert, 'top' for others
  const getFallback = () => {
    if (currentVar?.["type"] === "likert") return "net";
    return ActionMode === "explore" ? "top" : currentVar?.["keys"]?.[0];
  };

  // --- STYLES ---
  
  // Unified Button Style (Used for Top, Net, AND Specific Options now)
  const optionBtnStyle = (isActive) => ({
    textTransform: "none",
    // flexible layout for grid
    display: "flex",
    alignItems: "center",
    justifyContent: "center", 
    textAlign: "center",
    width: "100%",
    height: "100%", // Ensures equal height in grid rows
    minHeight: "3em", // Minimum touch target
    
    fontWeight: isActive ? 600 : 500,
    fontSize: "0.75rem", // Slightly smaller font to handle long text in 2 cols
    lineHeight: 1.2,
    whiteSpace: "normal", // Allows text wrapping
    
    boxShadow: "none",
    borderRadius: 2,
    border: "1px solid",
    borderColor: isActive ? "primary.main" : "divider",
    color: isActive ? "primary.main" : "text.secondary",
    bgcolor: isActive ? "rgba(25, 118, 210, 0.08)" : "background.paper",
    
    py: 1,
    px: 1,
    
    '&:hover': {
      bgcolor: isActive ? "rgba(25, 118, 210, 0.15)" : "action.hover",
      borderColor: isActive ? "primary.main" : "text.primary",
    }
  });

  // --- SUB-COMPONENTS ---
  const SectionLabel = ({ title, subtitle }) => (
    <Box sx={{ mt: 2, mb: 1, px: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block', mt: 0.2 }}>
            {subtitle}
        </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", px: 0.5 }}>
      
      {/* GLOBAL HEADER */}
  

      {/* ========================================================= */}
      {/* LIKERT LOGIC                                              */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "likert" && (
        <>
            {/* ZONE 1: AGGREGATES */}
            <SectionLabel 
                title="Summary Views" 
                subtitle="Aggregated data calculations."
            />
            
            <Box display="grid" gridTemplateColumns={ActionMode === "explore" ? "1fr 1fr" : "1fr"} gap={0.75}>
                {ActionMode === "explore" && (
                <Button
                    fullWidth
                    variant="outlined" 
                    sx={optionBtnStyle(preferenceFilter === "top")}
                    startIcon={<StarOutlineIcon sx={{fontSize: 18}} />}
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "top", "top"))}
                >
                    Top Option
                </Button>
                )}

                <Button
                    fullWidth
                    variant="outlined"
                    sx={optionBtnStyle(preferenceFilter === "net")}
                    startIcon={<EqualizerIcon sx={{fontSize: 18}} />} 
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "net", getFallback()))}
                >
                    Net Score
                </Button>
            </Box>

            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

            {/* ZONE 2: DETAILED LIST (NOW 2 COLUMNS) */}
            <SectionLabel 
                title="Individual Options" 
                subtitle="Select a single option to isolate."
            />

            <Box 
              display="grid" 
              gridTemplateColumns="1fr 1fr" 
              gap={0.75} // Tight spacing
              alignItems="stretch" // Ensures buttons match height in same row
            >
                {(currentVar?.["keys"] || []).map((key) => {
                    const isActive = preferenceFilter === key;
                    return (
                        <Tooltip
                            key={key}
                            title={currentVar?.["full_labels"]?.[key] || ""}
                            placement="left"
                            TransitionComponent={Fade}
                            arrow
                            disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                        >
                            <Button
                                sx={optionBtnStyle(isActive)}
                                onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                            >
                                {currentVar?.["labels"]?.[key]}
                            </Button>
                        </Tooltip>
                    );
                })}
            </Box>
        </>
      )}

      {/* ========================================================= */}
      {/* CATEGORICAL LOGIC                                         */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "categorical" && (
        <>
            {ActionMode === "explore" && (
                <>
                <SectionLabel
                    title="Rankings"
                    subtitle="Quickly view the highest performers."
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={0.5}>
                    {["top", "second", "third"].map((rank) => (
                        <Button
                            key={rank}
                            fullWidth
                            sx={optionBtnStyle(preferenceFilter === rank)}
                            onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, rank, "top"))}
                        >
                            {rank.charAt(0).toUpperCase() + rank.slice(1)}
                        </Button>
                    ))}
                </Box>
                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                </>
            )}

            <SectionLabel
                title="Specific Categories"
                subtitle="Isolate a single category."
            />

            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap={0.75}
              alignItems="stretch"
            >
                {sortedKeys.map((key) => {
                    const isActive = preferenceFilter === key;
                    return (
                        <Tooltip
                            key={key}
                            title={currentVar?.["full_labels"]?.[key] || ""}
                            placement="left"
                            TransitionComponent={Fade}
                            arrow
                            disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                        >
                            <Button
                                sx={optionBtnStyle(isActive)}
                                onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                            >
                                {currentVar?.["labels"]?.[key]}
                            </Button>
                        </Tooltip>
                    );
                })}
            </Box>
        </>
      )}

      {/* ========================================================= */}
      {/* PRICE ELASTICITY LOGIC                                    */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "price_elast" && (
        <Box sx={{ mt: 1 }}>
            <SectionLabel 
                title="Price Thresholds" 
                subtitle="Standard elasticity definitions."
            />
            {/* Segmented Control Look */}
            <Paper elevation={0} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 2 }}>
                <Stack direction={ActionMode === "explore" ? "row" : "column"} spacing={0.5}>
                {["expect", "expensive", "tooexpensive"].map((pol) => {
                    const val = currentVar?.["polarity"]?.[pol];
                    const isSelected = preferenceFilter === val;
                    
                    return (
                        <Tooltip
                            key={pol}
                            title={currentVar?.["full_labels"]?.[val] || ""}
                            placement="top"
                        >
                        <Button
                            fullWidth
                            sx={{
                                borderRadius: 1.5,
                                color: isSelected ? "text.primary" : "text.secondary",
                                bgcolor: isSelected ? "background.paper" : "transparent",
                                boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: "0.75rem",
                                textTransform: "none",
                                py: 0.8,
                                border: isSelected ? "1px solid" : "1px solid transparent",
                                borderColor: isSelected ? "divider" : "transparent",
                                '&:hover': { bgcolor: isSelected ? "background.paper" : "rgba(255,255,255,0.5)" }
                            }}
                            onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, val, currentVar?.["polarity"]?.expect))}
                        >
                            {currentVar?.["labels"]?.[val]}
                        </Button>
                        </Tooltip>
                    );
                })}
                </Stack>
            </Paper>
        </Box>
      )}
    </Box>
  );
};
export default QuestionFilterComponent;